import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { InfoIcon, AlertTriangle, ShieldCheck, Shield, Activity } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import { RiskMetric } from './types';

interface RiskOverviewProps {
  metrics: RiskMetric[];
  complianceScore?: number; // Optional compliance score
  loading: boolean;
}

export const RiskOverview: React.FC<RiskOverviewProps> = ({
  metrics = [],
  complianceScore, // No default needed, handled by conditional render
  loading = false,
}) => {

  const getRiskColor = (level: RiskMetric['level']): string => {
    switch (level) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-amber-500';
      case 'high': return 'bg-red-500';
      case 'info':
      default: return 'bg-blue-500';
    }
  };

  const getComplianceColor = (score: number | undefined): string => {
      if(score === undefined) return 'text-gray-500'; // Default or N/A color
      if (score >= 80) return 'text-green-600';
      if (score >= 60) return 'text-amber-600';
      return 'text-red-600';
  };

  const renderLoadingState = () => (
     <Card>
      <CardHeader>
        <Skeleton className="h-6 w-1/2 mb-1" />
        <Skeleton className="h-4 w-3/4" />
      </CardHeader>
      <CardContent className="space-y-5 pt-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
                <div className="flex justify-between">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-4 w-1/4" />
                </div>
                <Skeleton className="h-2 w-full" />
            </div>
          ))}
          {complianceScore !== undefined && <Skeleton className="h-6 w-1/2 mt-2" />}
      </CardContent>
    </Card>
  );

 const renderEmptyState = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Risk Overview</CardTitle>
        <CardDescription>Key risk indicators for your portfolio.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center h-48 text-center border border-dashed rounded-lg bg-gray-50">
            <Shield className="h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-500">No Risk Data</h3>
            <p className="text-sm text-gray-400 max-w-xs mt-1">
              Risk metrics will appear here once strategies are active and trades occur.
            </p>
        </div>
      </CardContent>
    </Card>
 );


  if (loading) {
    return renderLoadingState();
  }

  if (!loading && metrics.length === 0) {
      return renderEmptyState();
  }


  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium">Risk Overview</CardTitle>
           {complianceScore !== undefined && (
            <div className="flex items-center gap-1">
              {complianceScore >= 80 ? (
                <ShieldCheck className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className={`h-5 w-5 ${getComplianceColor(complianceScore)}`} />
              )}
              <span className={`font-medium text-sm ${getComplianceColor(complianceScore)}`}>
                {complianceScore}% Compliant
              </span>
            </div>
          )}
        </div>
         <CardDescription>Key risk indicators for your portfolio.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
            {metrics.map((metric) => (
              <div key={metric.id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                   <TooltipProvider delayDuration={100}>
                      <Tooltip>
                          <TooltipTrigger className="flex items-center gap-1 cursor-help">
                              <span className="font-medium text-gray-700">{metric.name}</span>
                              {metric.description && <InfoIcon className="h-3 w-3 text-gray-400" />}
                          </TooltipTrigger>
                          {metric.description && (
                             <TooltipContent className="max-w-xs text-xs">
                                 <p>{metric.description}</p>
                             </TooltipContent>
                           )}
                      </Tooltip>
                   </TooltipProvider>
                  <span className="font-semibold">{metric.value}</span>
                </div>
                {/* Display Progress only if value is a percentage or can be normalized 0-100 */}
                {typeof metric.value === 'string' && metric.value.includes('%') ? (
                     <Progress
                        value={parseFloat(metric.value.replace('%','').replace('-',''))} // Normalize value for progress
                        className="h-2"
                        indicatorClassName={getRiskColor(metric.level)}
                     />
                ) : typeof metric.value === 'number' && metric.id === 'sharpe' ? ( // Example for Sharpe
                     <Progress
                        value={(metric.value / 3) * 100} // Normalize Sharpe (0-3 scale example)
                        className="h-2"
                        indicatorClassName={getRiskColor(metric.level)}
                    />
                 ) : (
                    <div className="h-2 bg-gray-200 rounded-full mt-1">
                        <div className={`h-2 rounded-full ${getRiskColor(metric.level)}`} style={{ width: '50%' }}></div> {/* Default visualization */}
                    </div>
                )}
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
};
